import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { PageIssue } from "./IssueTable";
import type { IssuesSummary } from "./IssuesSummaryHeader";
import type { IssueSeverity } from "./SeverityBadge";
import { getCanonicalIssueSubtype } from "@/types/technicalAudit";

// ============================================================================
// Severity calculation (matches IssueTable.tsx)
// ============================================================================

function calculateSeverity(issue: PageIssue): IssueSeverity {
	if (issue.severity === "critical" || issue.severity === "warning" || issue.severity === "info") {
		return issue.severity;
	}

	switch (issue.issue_type) {
		case "title_meta": {
			const details = issue.issue_details;
			if (details.title_missing || details.meta_missing) return "critical";
			if (details.title_too_long || details.meta_too_long) return "warning";
			return "info";
		}
		case "h1": {
			const details = issue.issue_details;
			if (details.h1_missing) return "critical";
			if (details.multiple_h1s) return "warning";
			return "info";
		}
		case "h1_missing":
			return "critical";
		case "h1_multiple":
			return "warning";
		case "h1_too_long":
			return "info";
		case "alt_text":
			return "warning";
		case "redirect": {
			const details = issue.issue_details;
			const redirectCount = details.redirect_count as number;
			if (redirectCount > 3) return "critical";
			if (redirectCount > 1) return "warning";
			return "info";
		}
		case "broken_link": {
			const statusCode = issue.issue_details.status_code as number;
			if ([404, 410, 500, 502, 503].includes(statusCode)) return "critical";
			return "warning";
		}
		case "broken_internal":
			return "critical";
		case "canonical": {
			const issueSubtype = getCanonicalIssueSubtype(issue.issue_details);
			if (issueSubtype === "missing") return "critical";
			return "warning";
		}
		case "duplicate_title":
			return "warning";
		case "page_title_missing":
		case "meta_missing":
			return "critical";
		case "page_title_over_60_chars":
		case "meta_description_over_155_chars":
			return "warning";
		case "page_title_same_as_h1":
		case "page_title_below_30_chars":
		case "meta_description_below_70_chars":
			return "info";
		case "duplicate_meta":
			return "warning";
		case "thin_content": {
			const wordCount = issue.issue_details.word_count as number;
			if (wordCount < 100) return "critical";
			return "warning";
		}
		case "indexability":
			return "critical";
		case "slow_response": {
			const responseTime = issue.issue_details.response_time_ms as number;
			if (responseTime > 5000) return "critical";
			return "warning";
		}
		case "near_duplicate": {
			const score = issue.issue_details.similarity_score as number;
			if (score > 0.95) return "critical";
			return "warning";
		}
		case "orphan_page":
			return "warning";
		case "schema_error":
			return "warning";
		case "missing_schema":
			return "warning";
		case "missing_og_tags":
			return "warning";
		case "mixed_content":
			return "warning";
		case "redirect_loop":
			return "critical";
		case "title_too_short":
			return "warning";
		case "serp_title_truncated":
			return "warning";
		case "serp_meta_truncated":
			return "info";
		case "multiple_meta":
			return "warning";
		case "duplicate_h1":
			return "warning";
		case "low_readability":
			return "info";
		case "low_text_ratio":
			return "info";
		case "stale_content":
			return "info";
		case "broken_external": {
			const extStatusCode = issue.issue_details.status_code as number;
			if ([404, 410, 500, 502, 503].includes(extStatusCode)) return "critical";
			return "warning";
		}
		case "links_to_broken":
			// Source-page-anchored (lists N broken internal targets); high severity
			// like broken_internal, independent of individual status codes.
			return "critical";
		case "deep_page": {
			const depth = issue.issue_details.click_depth as number;
			if (depth > 5) return "critical";
			return "warning";
		}
		case "large_image": {
			const imgSize = issue.issue_details.file_size_bytes as number;
			if (imgSize > 2_000_000) return "critical";
			return "warning";
		}
		case "broken_image":
			return "critical";
		case "non_nextgen_image":
			return "info";
		case "large_page": {
			const pgSize = issue.issue_details.page_size_bytes as number;
			if (pgSize > 5_000_000) return "critical";
			return "warning";
		}
		case "unsafe_crossorigin":
			return "info";
		case "nofollow_internal":
			return "warning";
		case "low_internal_links":
			return "warning";
		case "missing_hsts":
			return "info";
		case "hreflang_error":
			return "warning";
		case "sitemap_error":
			return "warning";
		case "missing_from_sitemap":
			return "warning";
		case "performance_diagnostics":
			return "warning";
		case "keyword_cannibalization":
			return "warning";
		case "image_missing_dimensions":
			return "info";
		case "core_web_vitals":
			return "warning";
		default:
			return "info";
	}
}

const issueTypeLabels: Record<string, string> = {
	title_meta: "Title & Meta",
	h1: "H1 Heading",
	h1_missing: "Missing H1",
	h1_multiple: "Multiple H1 Tags",
	h1_too_long: "H1 Too Long",
	alt_text: "Image Alt Text",
	redirect: "Redirects",
	broken_link: "Broken Link",
	broken_internal: "Broken Internal URL",
	canonical: "Canonicals",
	duplicate_title: "Duplicate SEO Title Tags",
	page_title_same_as_h1: "Title Matches H1",
	page_title_missing: "Missing SEO Title",
	page_title_over_60_chars: "SEO Title Too Long",
	page_title_below_30_chars: "SEO Title Too Short",
	duplicate_meta: "Duplicate Descriptions",
	meta_missing: "Missing Meta Description",
	meta_description_over_155_chars: "Meta Description Too Long",
	meta_description_below_70_chars: "Meta Description Too Short",
	thin_content: "Thin Content",
	indexability: "Indexability",
	slow_response: "Slow Response",
	near_duplicate: "Near-Duplicate Content",
	orphan_page: "Orphan Page",
	schema_error: "Schema Validation Error",
	missing_schema: "Missing Schema",
	schema_validation_error: "Schema Validation Errors",
	missing_og_tags: "Missing Open Graph Tags",
	mixed_content: "Mixed Content",
	redirect_loop: "Redirect Loop",
	title_too_short: "Title Too Short",
	serp_title_truncated: "SERP Title Truncated",
	serp_meta_truncated: "SERP Description Truncated",
	multiple_meta: "Multiple Meta Descriptions",
	duplicate_h1: "Duplicate H1",
	low_readability: "Low Readability",
	low_text_ratio: "Low Text-to-HTML Ratio",
	stale_content: "Stale Content",
	broken_external: "Broken External Link",
	deep_page: "Deep Page",
	large_image: "Oversized Image",
	broken_image: "Broken Image",
	non_nextgen_image: "Non-Next-Gen Image",
	large_page: "Oversized Page",
	unsafe_crossorigin: "Unsafe Cross-Origin Link",
	nofollow_internal: "Nofollow Internal Link",
	low_internal_links: "Low Internal Links",
	missing_hsts: "Missing HSTS",
	hreflang_error: "Hreflang Error",
	sitemap_error: "Sitemap Issue",
	missing_from_sitemap: "Missing from Sitemap",
	performance_diagnostics: "Performance Diagnostics",
	keyword_cannibalization: "Keyword Cannibalization",
	image_missing_dimensions: "Image Missing Dimensions",
	core_web_vitals: "Core Web Vitals",
};

function getIssueDescription(issue: PageIssue): string {
	const d = issue.issue_details;
	switch (issue.issue_type) {
		case "title_meta": {
			const problems: string[] = [];
			if (d.title_missing) problems.push("Title missing");
			else if (d.title_too_long) problems.push("Title too long");
			else if (d.title_too_short) problems.push("Title too short");
			if (d.meta_missing) problems.push("Meta description missing");
			else if (d.meta_too_long) problems.push("Meta description too long");
			else if (d.meta_too_short) problems.push("Meta description too short");
			return problems.join("; ") || "Title/meta issue";
		}
		case "h1":
		case "h1_missing":
		case "h1_multiple":
		case "h1_too_long": {
			if (d.h1_missing) return "Missing H1 heading";
			if (d.multiple_h1s) return `Multiple H1 headings (${d.h1_count})`;
			if (d.h1_too_long) return "H1 too long";
			return "H1 issue";
		}
		case "alt_text":
			return `Missing alt text: ${d.image_filename || d.image_url || "unknown image"}`;
		case "redirect": {
			const chain = d.redirect_chain as string[] | undefined;
			return `${d.redirect_count} redirect(s)${chain ? ` -> ${d.final_url}` : ""}`;
		}
		case "broken_link":
			return `Broken link: ${d.target_url || "unknown"} (${d.status_code || "?"})`;
		case "broken_internal":
			return `Broken internal URL: ${issue.page_url || "unknown"} (${d.status_code || issue.status_code || "?"})`;
		case "canonical":
			return `Canonical ${getCanonicalIssueSubtype(d)}: ${d.page_url || issue.page_url || ""}`;
		case "duplicate_title":
			return `"${d.title || ""}" duplicated across ${d.count || 0} pages`;
		case "page_title_same_as_h1":
			return `SEO title matches H1: "${d.current_title || d.title_text || ""}"`;
		case "page_title_missing":
			return "SEO title is missing";
		case "page_title_over_60_chars":
			return `SEO title "${d.current_title || ""}" is ${d.title_length || 0} chars (max: ${d.max_length || 60})`;
		case "page_title_below_30_chars":
			return `SEO title "${d.current_title || ""}" is ${d.title_length || 0} chars (min: ${d.min_length || 30})`;
		case "duplicate_meta":
			return `Meta description duplicated across ${d.count || 0} pages`;
		case "meta_missing":
			return "Meta description is missing";
		case "meta_description_over_155_chars":
			return `Meta description is ${d.meta_length || 0} chars (max: ${d.max_length || 155})`;
		case "meta_description_below_70_chars":
			return `Meta description is ${d.meta_length || 0} chars (min: ${d.min_length || 70})`;
		case "thin_content":
			return `${d.word_count || 0} words (threshold: ${d.threshold || 300})`;
		case "indexability":
			return `${d.indexability_status || "Non-indexable"}: ${d.reason || ""}`;
		case "slow_response":
			return `${d.response_time_ms || 0}ms (threshold: ${d.threshold_ms || 3000}ms)`;
		case "near_duplicate":
			return `${Math.round((d.similarity_score as number || 0) * 100)}% similar to ${d.similar_url || "unknown"}`;
		case "orphan_page":
			return `No incoming internal links (${d.incoming_links || 0} found)`;
		case "schema_error":
			return `${d.schema_type || "Schema"}: ${(d.errors as string[] || []).join("; ") || "validation error"}`;
		case "missing_schema": {
			const missingTypes = (d.missing_schema_types as string[] | undefined) || [];
			return `Missing ${missingTypes.join(", ") || "page-appropriate schema"}`;
		}
		case "schema_validation_error": {
			const errCount = (d.error_count as number | undefined) || 0;
			const types = (d.schema_types as string[] | undefined) || [];
			return `${errCount} validation error${errCount === 1 ? "" : "s"} in ${
				types.join(", ") || "structured data"
			}`;
		}
		case "missing_og_tags": {
			const missingTags = (d.missing as string[] | undefined) || [];
			return `Missing ${missingTags.join(", ") || "Open Graph tags"}`;
		}
		case "mixed_content":
			return `Insecure ${d.resource_type || "resource"}: ${d.insecure_url || "unknown"}`;
		case "redirect_loop":
			return `Redirect loop detected at ${d.loop_detected_at || "unknown"}`;
		case "title_too_short":
			return `Title "${d.current_title || ""}" is ${d.title_length || 0} chars (min: ${d.min_length || 30})`;
		case "serp_title_truncated":
			return `Title ${d.pixel_width || 0}px wide (max: ${d.max_pixel_width || 600}px)`;
		case "serp_meta_truncated":
			return `Meta description ${d.character_length || 0} chars (max: ${d.max_length || 160})`;
		case "multiple_meta":
			return `${d.count || 0} meta descriptions found`;
		case "duplicate_h1":
			return `"${d.h1_text || ""}" duplicated across ${d.count || 0} pages`;
		case "low_readability":
			return `Readability score: ${d.readability_score || 0} (${d.grade_level || "unknown"}, threshold: ${d.threshold || 0})`;
		case "low_text_ratio":
			return `Text ratio: ${Math.round((d.text_ratio as number || 0) * 100)}% (threshold: ${Math.round((d.threshold as number || 0) * 100)}%)`;
		case "stale_content":
			return `Last updated ${d.days_since_update || 0} days ago (threshold: ${d.threshold_days || 365} days)`;
		case "broken_external":
			return `Broken external link: ${d.target_url || "unknown"} (${d.status_code || "?"})`;
		case "deep_page":
			return `${d.click_depth || 0} clicks from homepage (max: ${d.max_depth || 3})`;
		case "large_image":
			return `Image ${Math.round((d.file_size_bytes as number || 0) / 1024)}KB (max: ${Math.round((d.max_size_bytes as number || 0) / 1024)}KB)`;
		case "broken_image":
			return `Broken image: ${d.image_url || d.target_url || "unknown"} (${d.status_code || "?"})`;
		case "non_nextgen_image":
			return `${d.current_format || "unknown"} format, recommend ${d.recommended_format || "WebP"}`;
		case "large_page":
			return `Page size ${Math.round((d.page_size_bytes as number || 0) / 1024)}KB (max: ${Math.round((d.max_size_bytes as number || 0) / 1024)}KB)`;
		case "unsafe_crossorigin":
			return `Unsafe link to ${d.target_url || "unknown"} (rel: ${d.rel_attributes || "none"})`;
		case "nofollow_internal":
			return `Nofollow on internal link to ${d.target_url || "unknown"}`;
		case "low_internal_links":
			return `${d.internal_link_count || 0} internal links (threshold: ${d.threshold || 3})`;
		case "missing_hsts":
			return "Missing HTTP Strict Transport Security header";
		case "hreflang_error":
			return `Hreflang issue: ${d.hreflang_url || "unknown"} (${d.target_status || d.status_code || "?"})`;
		case "sitemap_error":
			return `Sitemap issue: ${d.reason || d.indexability || d.status_code || "review needed"}`;
		case "missing_from_sitemap":
			return `Indexable page not listed in the XML sitemap (sitemap lists ${d.sitemap_url_count ?? "?"} URLs)`;
		case "performance_diagnostics":
			return `${d.diagnostic || "Performance diagnostic"}${d.impact ? `: ${d.impact}` : ""}`;
		case "keyword_cannibalization":
			return `${d.competing_count || 0} pages competing for the same primary keyword`;
		case "image_missing_dimensions":
			return `Image missing ${((d.missing_attributes as string[]) || []).join("/") || "width/height"}: ${d.image_url || "unknown"}`;
		case "core_web_vitals":
			return `${d.metric || "Metric"} ${d.value ?? "?"} (threshold: ${d.threshold ?? "?"})`;
		default:
			return "Issue detected";
	}
}

function getProposedFixText(issue: PageIssue): string {
	if (!issue.proposed_fix) return "";
	const fix = issue.proposed_fix;
	const parts: string[] = [];
	const seoTitle = fix.new_seo_title || fix.new_title;
	if (seoTitle) parts.push(`SEO Title: ${seoTitle}`);
	if (fix.new_meta_description) parts.push(`Meta: ${fix.new_meta_description}`);
	if (fix.new_h1) parts.push(`H1: ${fix.new_h1}`);
	if (fix.new_alt_text) parts.push(`Alt: ${fix.new_alt_text}`);
	if (fix.recommended_target) parts.push(`Target: ${fix.recommended_target}`);
	if (fix.recommendation) parts.push(`${fix.recommendation}`);
	return parts.join("; ");
}

// ============================================================================
// CSV Export
// ============================================================================

export function exportCSV(issues: PageIssue[], tenantName: string) {
	const headers = [
		"Page URL",
		"Issue Type",
		"Severity",
		"Status",
		"Content Type",
		"Description",
		"Proposed Fix",
		"Created At",
	];

	const rows = issues.map((issue) => [
		issue.page_url,
		issueTypeLabels[issue.issue_type] || issue.issue_type,
		calculateSeverity(issue),
		issue.fix_status,
		issue.content_type || "",
		getIssueDescription(issue),
		getProposedFixText(issue),
		new Date(issue.created_at).toLocaleDateString(),
	]);

	const csvContent = [headers, ...rows]
		.map((row) =>
			row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
		)
		.join("\n");

	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = `audit-report-${tenantName}-${new Date().toISOString().slice(0, 10)}.csv`;
	link.click();
	URL.revokeObjectURL(url);
}

// ============================================================================
// PDF Export
// ============================================================================

export function exportPDF(
	issues: PageIssue[],
	summary: IssuesSummary,
	tenantName: string,
) {
	const doc = new jsPDF({ orientation: "landscape" });
	const pageWidth = doc.internal.pageSize.getWidth();
	const now = new Date();

	// Title
	doc.setFontSize(20);
	doc.text("Technical SEO Audit Report", 14, 22);

	// Subtitle info
	doc.setFontSize(11);
	doc.setTextColor(100);
	doc.text(`Site: ${tenantName}`, 14, 32);
	doc.text(`Generated: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`, 14, 38);
	doc.text(`Total Issues: ${summary.total}`, 14, 44);

	// Summary boxes
	const summaryY = 52;
	const boxWidth = 55;
	const boxHeight = 22;
	const boxes = [
		{ label: "Critical", count: summary.by_severity.critical || 0, color: [220, 38, 38] as [number, number, number] },
		{ label: "Warning", count: summary.by_severity.warning || 0, color: [234, 179, 8] as [number, number, number] },
		{ label: "Info", count: summary.by_severity.info || 0, color: [59, 130, 246] as [number, number, number] },
	];

	boxes.forEach((box, i) => {
		const x = 14 + i * (boxWidth + 8);
		doc.setFillColor(...box.color);
		doc.roundedRect(x, summaryY, boxWidth, boxHeight, 2, 2, "F");
		doc.setTextColor(255);
		doc.setFontSize(14);
		doc.text(String(box.count), x + boxWidth / 2, summaryY + 10, { align: "center" });
		doc.setFontSize(9);
		doc.text(box.label, x + boxWidth / 2, summaryY + 17, { align: "center" });
	});

	// Status breakdown
	const statusX = 14 + 3 * (boxWidth + 8) + 10;
	doc.setTextColor(60);
	doc.setFontSize(10);
	doc.text("By Status:", statusX, summaryY + 6);
		const statuses = [
			{ label: "Pending", count: summary.by_status.pending || 0 },
			{ label: "Suggested", count: summary.by_status.approved || 0 },
			{ label: "Skipped", count: summary.by_status.rejected || 0 },
			{ label: "Applied", count: summary.by_status.applied || 0 },
			{ label: "Recommended", count: summary.by_status.recommended || 0 },
		];
	statuses.forEach((s, i) => {
		doc.text(`${s.label}: ${s.count}`, statusX, summaryY + 14 + i * 5);
	});

	// Issues table
	const tableData = issues.map((issue) => [
		issue.page_url.length > 60 ? issue.page_url.slice(0, 57) + "..." : issue.page_url,
		issueTypeLabels[issue.issue_type] || issue.issue_type,
		calculateSeverity(issue).toUpperCase(),
		issue.fix_status,
		getIssueDescription(issue),
		getProposedFixText(issue).slice(0, 80) || "-",
	]);

	autoTable(doc, {
		startY: summaryY + boxHeight + 12,
		head: [["Page URL", "Type", "Severity", "Status", "Description", "Proposed Fix"]],
		body: tableData,
		styles: { fontSize: 7, cellPadding: 2 },
		headStyles: { fillColor: [31, 41, 55], fontSize: 8 },
		columnStyles: {
			0: { cellWidth: 70 },
			1: { cellWidth: 28 },
			2: { cellWidth: 20 },
			3: { cellWidth: 20 },
			4: { cellWidth: 70 },
			5: { cellWidth: 60 },
		},
		didParseCell: (data) => {
			if (data.section === "body" && data.column.index === 2) {
				const val = String(data.cell.raw).toLowerCase();
				if (val === "critical") data.cell.styles.textColor = [220, 38, 38];
				else if (val === "warning") data.cell.styles.textColor = [180, 130, 0];
				else if (val === "info") data.cell.styles.textColor = [59, 130, 246];
			}
		},
		// Footer
		didDrawPage: (data) => {
			const pageCount = doc.getNumberOfPages();
			doc.setFontSize(8);
			doc.setTextColor(150);
			doc.text(
				`Page ${data.pageNumber} of ${pageCount}`,
				pageWidth - 14,
				doc.internal.pageSize.getHeight() - 10,
				{ align: "right" },
			);
		},
	});

	doc.save(`audit-report-${tenantName}-${now.toISOString().slice(0, 10)}.pdf`);
}
